import * as React from 'react';
import styled from '../../theme/styled-components';
import { rem, rgba } from 'polished';
import { connect } from 'react-redux';
import { __ } from '@bart.sk-eshop/react-i18n';
import { ErrorAlert, Loader } from '@bart.sk-eshop/eshop-defaults';
import API from '../../services/API';
import { LogoutButton } from './MyAccount';
import { langSelector } from '../App/selectors';

const Wrapper = styled.div`
  width: 100%;
  max-width: ${rem(720)};
  padding: ${rem(25)};
  background-color: white;
  margin: ${rem(30)} auto;
  border-radius: ${rem(4)};
`;

const Title = styled.h1`
  font-size: ${rem(30)};
  font-weight: 400;
  font-family: ${({ theme }) => theme.font.primary};
  margin-bottom: ${rem(24)};
  line-height: ${rem(38)};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Intro = styled.p`
  font-family: ${({ theme }) => theme.font.secondary};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${rem(15)};
  line-height: ${rem(22)};
  margin-bottom: ${rem(24)};
`;

const SectionTitle = styled.h2`
  font-size: ${rem(18)};
  font-weight: 500;
  font-family: ${({ theme }) => theme.font.primary};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: ${rem(24)} 0 ${rem(12)};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem(12)};
  margin-bottom: ${rem(12)};

  ${({ theme }) => theme.mediab.l925`
    grid-template-columns: 1fr;
  `}
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${rem(12)};
`;

const Label = styled.label`
  font-family: ${({ theme }) => theme.font.secondary};
  font-size: ${rem(13)};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${rem(6)};
`;

const Required = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  margin-left: ${rem(2)};
`;

const inputStyles = `
  display: block;
  width: 100%;
  background: #fff;
  border-radius: ${rem(2)};
  padding: ${rem(14)} ${rem(16)};
  border: 1px solid #cccccc;
  font-size: ${rem(15)};
  line-height: ${rem(22)};
  font-weight: 400;

  &:disabled {
    cursor: not-allowed;
    background: #f5f5f5;
  }
`;

const TextInput = styled.input`
  ${inputStyles}
  height: ${rem(48)};
  font-family: ${({ theme }) => theme.font.secondary};
  color: ${({ theme }) => theme.colors.textPrimary};

  &::placeholder {
    color: ${({ theme }) => theme.colors.placeholder};
  }
`;

const TextArea = styled.textarea`
  ${inputStyles}
  min-height: ${rem(120)};
  font-family: ${({ theme }) => theme.font.secondary};
  color: ${({ theme }) => theme.colors.textPrimary};
  resize: vertical;

  &::placeholder {
    color: ${({ theme }) => theme.colors.placeholder};
  }
`;

const ButtonWrapper = styled.div`
  margin-top: ${rem(8)};

  & > button {
    margin-top: ${rem(8)};
  }
`;

const SuccessBox = styled.div`
  padding: ${rem(32)} ${rem(20)};
  text-align: center;
  font-family: ${({ theme }) => theme.font.secondary};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const SuccessIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${rem(16)};
`;

const SuccessTitle = styled.h1`
  font-size: ${rem(28)};
  font-weight: 600;
  font-family: ${({ theme }) => theme.font.primary};
  color: ${({ theme }) => theme.color.success};
  margin-bottom: ${rem(16)};
  line-height: ${rem(36)};
`;

const SuccessCheckSvg = styled.svg`
  circle {
    fill: ${({ theme }) => rgba(theme.color.success, 0.12)};
    stroke: ${({ theme }) => theme.color.success};
  }
  path {
    stroke: ${({ theme }) => theme.color.success};
  }
`;

const SuccessCheckIcon: React.FC = () => (
  <SuccessCheckSvg
    width="64"
    height="64"
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="32" cy="32" r="30" strokeWidth="2" />
    <path
      d="M20 33 L29 42 L45 24"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </SuccessCheckSvg>
);

interface FormState {
  customer_email: string;
  customer_name: string;
  customer_surname: string;
  customer_phone: string;
  street: string;
  city: string;
  zip: string;
  country: string;
  order_number: string;
  bank_account: string;
  items_text: string;
  note: string;
}

interface Props {
  lang: string;
}

interface State {
  form: FormState;
  fetching: boolean;
  error: string;
  submitted: boolean;
}

const initialForm: FormState = {
  customer_email: '',
  customer_name: '',
  customer_surname: '',
  customer_phone: '',
  street: '',
  city: '',
  zip: '',
  country: '',
  order_number: '',
  bank_account: '',
  items_text: '',
  note: '',
};

class ContractWithdrawal extends React.Component<Props, State> {
  public state: State = {
    form: { ...initialForm },
    fetching: false,
    error: '',
    submitted: false,
  };

  private onChange = (key: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = e.target.value;
    this.setState(prev => ({ form: { ...prev.form, [key]: value } }));
  };

  private validate = (): string => {
    const { form } = this.state;
    const required: Array<{ key: keyof FormState; label: string }> = [
      { key: 'customer_email', label: __('E-mail') },
      { key: 'customer_name', label: __('Meno') },
      { key: 'customer_surname', label: __('Priezvisko') },
      { key: 'order_number', label: __('Číslo objednávky') },
      { key: 'bank_account', label: __('IBAN') },
      { key: 'items_text', label: __('Tovar, ktorého sa odstúpenie týka') },
    ];

    for (const { key, label } of required) {
      if (!form[key] || !form[key].trim()) {
        return `${__('Vyplňte prosím pole')}: ${label}`;
      }
    }

    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(form.customer_email)) {
      return __('Zadajte platný e-mail');
    }

    return '';
  };

  private onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = this.validate();
    if (validationError) {
      this.setState({ error: validationError });
      return;
    }

    const { form } = this.state;
    const body: Parameters<typeof API.createWithdrawal>[1] = {
      customer_email: form.customer_email.trim(),
      customer_name: form.customer_name.trim(),
      customer_surname: form.customer_surname.trim(),
      order_number: form.order_number.trim(),
      bank_account: form.bank_account.trim(),
      items_text: form.items_text.trim(),
      lang: this.props.lang || 'sk',
    };

    const assignOptional = (
      key: 'customer_phone' | 'street' | 'city' | 'zip' | 'country' | 'note',
      value: string,
    ) => {
      if (value && value.trim()) {
        body[key] = value.trim();
      }
    };

    assignOptional('customer_phone', form.customer_phone);
    assignOptional('street', form.street);
    assignOptional('city', form.city);
    assignOptional('zip', form.zip);
    assignOptional('country', form.country);
    assignOptional('note', form.note);

    try {
      this.setState({ fetching: true, error: '' });
      await API.createWithdrawal({}, body);
      this.setState({ submitted: true, fetching: false });
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (exp) {
      this.setState({
        fetching: false,
        error: exp?.details?.description || __('Nastala chyba pri odoslaní'),
      });
    }
  };

  public render() {
    const { form, fetching, error, submitted } = this.state;

    if (submitted) {
      return (
        <Wrapper>
          <SuccessBox>
            <SuccessIconWrapper>
              <SuccessCheckIcon />
            </SuccessIconWrapper>
            <SuccessTitle>{__('Žiadosť bola odoslaná')}</SuccessTitle>
            <p>
              {__(
                'Na váš e-mail sme poslali potvrdzujúci odkaz. Kliknutím naň dokončíte odstúpenie od zmluvy.',
              )}
            </p>
          </SuccessBox>
        </Wrapper>
      );
    }

    return (
      <Loader loading={fetching}>
        <Wrapper>
          <Title>{__('Odstúpenie od zmluvy')}</Title>
          <Intro>
            {__(
              'Vyplňte nasledujúci formulár pre odstúpenie od kúpnej zmluvy. Na e-mail vám zašleme potvrdzujúci odkaz.',
            )}
          </Intro>

          <form onSubmit={this.onSubmit} noValidate={true} autoComplete="on">
            <SectionTitle>{__('Kontaktné údaje')}</SectionTitle>
            <Row>
              <Field>
                <Label>
                  {__('Meno')}
                  <Required>*</Required>
                </Label>
                <TextInput
                  type="text"
                  name="given-name"
                  autoComplete="given-name"
                  value={form.customer_name}
                  onChange={this.onChange('customer_name')}
                  required={true}
                />
              </Field>
              <Field>
                <Label>
                  {__('Priezvisko')}
                  <Required>*</Required>
                </Label>
                <TextInput
                  type="text"
                  name="family-name"
                  autoComplete="family-name"
                  value={form.customer_surname}
                  onChange={this.onChange('customer_surname')}
                  required={true}
                />
              </Field>
            </Row>
            <Row>
              <Field>
                <Label>
                  {__('E-mail')}
                  <Required>*</Required>
                </Label>
                <TextInput
                  type="email"
                  name="email"
                  autoComplete="email"
                  inputMode="email"
                  value={form.customer_email}
                  onChange={this.onChange('customer_email')}
                  required={true}
                />
              </Field>
              <Field>
                <Label>{__('Telefón')}</Label>
                <TextInput
                  type="tel"
                  name="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  value={form.customer_phone}
                  onChange={this.onChange('customer_phone')}
                />
              </Field>
            </Row>

            <SectionTitle>{__('Adresa')}</SectionTitle>
            <Field>
              <Label>{__('Ulica a číslo')}</Label>
              <TextInput
                type="text"
                name="street-address"
                autoComplete="street-address"
                value={form.street}
                onChange={this.onChange('street')}
              />
            </Field>
            <Row>
              <Field>
                <Label>{__('Mesto')}</Label>
                <TextInput
                  type="text"
                  name="address-level2"
                  autoComplete="address-level2"
                  value={form.city}
                  onChange={this.onChange('city')}
                />
              </Field>
              <Field>
                <Label>{__('PSČ')}</Label>
                <TextInput
                  type="text"
                  name="postal-code"
                  autoComplete="postal-code"
                  inputMode="numeric"
                  value={form.zip}
                  onChange={this.onChange('zip')}
                />
              </Field>
            </Row>
            <Field>
              <Label>{__('Krajina')}</Label>
              <TextInput
                type="text"
                name="country-name"
                autoComplete="country-name"
                value={form.country}
                onChange={this.onChange('country')}
              />
            </Field>

            <SectionTitle>{__('Objednávka')}</SectionTitle>
            <Field>
              <Label>
                {__('Číslo objednávky')}
                <Required>*</Required>
              </Label>
              <TextInput
                type="text"
                name="order_number"
                autoComplete="off"
                value={form.order_number}
                onChange={this.onChange('order_number')}
                required={true}
              />
            </Field>

            <Field>
              <Label>
                {__('Tovar, ktorého sa odstúpenie týka')}
                <Required>*</Required>
              </Label>
              <TextArea
                name="items_text"
                autoComplete="off"
                value={form.items_text}
                onChange={this.onChange('items_text')}
                placeholder={__('Uveďte zoznam tovaru (názov, množstvo)…')}
                required={true}
              />
            </Field>

            <SectionTitle>{__('Vrátenie peňazí')}</SectionTitle>
            <Field>
              <Label>
                {__('IBAN pre vrátenie peňazí')}
                <Required>*</Required>
              </Label>
              <TextInput
                type="text"
                name="bank_account"
                autoComplete="off"
                inputMode="text"
                value={form.bank_account}
                onChange={this.onChange('bank_account')}
                placeholder="SK00 0000 0000 0000 0000 0000"
                required={true}
              />
            </Field>

            <Field>
              <Label>{__('Poznámka')}</Label>
              <TextArea
                name="note"
                autoComplete="off"
                value={form.note}
                onChange={this.onChange('note')}
              />
            </Field>

            {error && <ErrorAlert message={error} />}

            <ButtonWrapper>
              <LogoutButton type="submit" disabled={fetching}>
                {__('Odoslať žiadosť')}
              </LogoutButton>
            </ButtonWrapper>
          </form>
        </Wrapper>
      </Loader>
    );
  }
}

const mapStateToProps = state => ({
  lang: langSelector(state),
});

export default connect(mapStateToProps)(ContractWithdrawal);
